import {
	ChoiceSelectField,
	cn,
	Forms9BoxRating,
	FormsAverage,
	FormsCheckbox,
	FormsDataViewer,
	FormsDivider,
	FormsExternalLink,
	FormsHeader,
	FormsImage,
	FormsInstructions,
	FormsNumberField,
	FormsParagraph,
	FormsRating,
	FormsReviewViewer,
	FormsSelectField,
	FormsTextArea,
	FormsTextField,
	getColumns,
	groupChildrenByColumn,
	Text,
} from '@viapeople/components'
import { useMemo } from 'react'
import { type Control, Controller, useWatch } from 'react-hook-form'
import type { FilteredComponent, Report } from '../types/preview'
import { FormsAggregationPreview } from './formComponents/FormsAggregationPreview'
import { FormsExternalDataView } from './formComponents/FormsExternalDataView'

type FormValues = Record<string, string | number | boolean>

interface PreviewComponentRendererProps {
	component: FilteredComponent
	control: Control<FormValues>
	allComponents?: FilteredComponent[]
}

/**
 * Simple report display component for preview mode
 */
function PreviewReports({ reports, showAverage = false }: { reports: Report[]; showAverage?: boolean }) {
	return (
		<div className='bg-neutral-50 border border-neutral-200 rounded-md p-4 mt-2'>
			<Text variant='small' weight='semibold' className='text-neutral-600 mb-2'>
				Sample Report Data (Preview)
			</Text>
			{reports.map((report, reportIndex) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: report items have no stable identifier
				<div key={reportIndex} className='space-y-2'>
					{report.submissions.map((submission) => (
						<div
							key={submission.label}
							className='flex justify-between items-start py-1 border-b border-neutral-100 last:border-0'>
							<Text variant='small' className='text-neutral-500'>
								{submission.label}
							</Text>
							<Text variant='small' className='text-neutral-700 max-w-[60%] text-right'>
								{submission.value}
							</Text>
						</div>
					))}
					{showAverage && report.average && (
						<div className='flex justify-between items-center pt-2 border-t border-neutral-200'>
							<Text variant='small' weight='semibold' className='text-neutral-600'>
								Average
							</Text>
							<Text variant='small' weight='semibold' className='text-primary-600'>
								{report.average}
							</Text>
						</div>
					)}
				</div>
			))}
		</div>
	)
}

/**
 * Container component for sections and questions
 */
function PreviewContainerComponent({ component, control, allComponents }: PreviewComponentRendererProps) {
	const props = component.custom as {
		label: string
		labelColor: string
		labelWeight?: 'regular' | 'semibold' | 'hidden'
		description: string
		descriptionColor: string
		isSection: boolean
		showResponseAverage?: boolean
		backgroundColor?: string
	}

	const columns = getColumns(component.custom as Record<string, unknown>)

	// Calculate average for questions with numeric fields
	const numericFields = useMemo(() => {
		const fields: {
			id: string
			type: 'rating' | 'select' | 'number'
			reportOnly?: boolean
			reports?: Report[]
		}[] = []

		const collectFields = (comp: FilteredComponent) => {
			if (comp.name === 'rating' || comp.name === 'select' || comp.name === 'number') {
				fields.push({
					id: comp.id,
					type: comp.name as 'rating' | 'select' | 'number',
					reportOnly: comp.reportOnly,
					reports: comp.mockReports,
				})
			}
			if (comp.children) {
				comp.children.forEach(collectFields)
			}
		}

		if (component.children) {
			component.children.forEach(collectFields)
		}

		return fields
	}, [component.children])

	const watchedValues = useWatch({
		control,
		name: numericFields.map((f) => f.id),
	})

	const average = useMemo(() => {
		if (numericFields.length === 0) return null

		const values: number[] = []

		numericFields.forEach((field, index) => {
			if (field.reports && field.reports.length > 0) {
				field.reports.forEach((report: Report) => {
					if (report.average && report.submissions) {
						const numericValue = Number.parseFloat(report.average)
						const submissionCount = report.submissions.length
						if (!Number.isNaN(numericValue) && Number.isFinite(numericValue) && submissionCount > 0) {
							for (let i = 0; i < submissionCount; i++) {
								values.push(numericValue)
							}
						}
					}
				})
			} else {
				const watchedValue = watchedValues?.[index]
				if (watchedValue !== undefined && watchedValue !== null && watchedValue !== '') {
					const numericValue = Number.parseFloat(String(watchedValue))
					if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
						values.push(numericValue)
					}
				}
			}
		})

		if (values.length === 0) return null
		return (values.reduce((acc, val) => acc + val, 0) / values.length).toFixed(2)
	}, [watchedValues, numericFields])

	return (
		<div
			className={cn(
				'component-section flex flex-col pl-6 gap-4',
				props.isSection && 'border rounded-lg border-neutral-200 py-4',
				props.backgroundColor && 'rounded-md',
			)}
			style={{ backgroundColor: props.backgroundColor || undefined }}>
			<Text
				variant='h6'
				weight={(props.labelWeight !== 'hidden' && props.labelWeight) || null}
				className={cn(`text-[${props.labelColor}]`, props.labelWeight === 'hidden' && 'hidden')}>
				{props.label}
			</Text>
			{props.description && (
				<Text variant='small' weight='regular' className={cn(`text-[${props.descriptionColor}]`)}>
					{props.description}
				</Text>
			)}
			{columns > 1 ? (
				<div
					className='component-section-content grid gap-4 px-6 pt-5'
					style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
					{groupChildrenByColumn(component.children || [], columns).map((colChildren, colIdx) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: columns are positional with no stable identifier
						<div key={colIdx} className='flex flex-col gap-2'>
							{colChildren.map((child) => (
								<PreviewComponentRenderer
									key={child.id}
									component={child}
									control={control}
									allComponents={allComponents}
								/>
							))}
						</div>
					))}
				</div>
			) : (
				<div className='component-section-content flex flex-col px-6 gap-2 pt-5'>
					{component.children?.map((child) => (
						<PreviewComponentRenderer
							key={child.id}
							component={child}
							control={control}
							allComponents={allComponents}
						/>
					))}
				</div>
			)}

			{!props.isSection &&
				average !== null &&
				numericFields.length > 0 &&
				props.label &&
				props.showResponseAverage !== false && (
					<>
						<div className='w-full h-px bg-neutral-200 mx-6' style={{ width: 'calc(100% - 3rem)' }} />
						<div className='px-6 pb-2'>
							<Text variant='small' className='text-neutral-600'>
								<span className='font-medium'>Average {props.label}:</span> {average}
							</Text>
						</div>
					</>
				)}
		</div>
	)
}

const PreviewRepeatableComponent = ({ component, control }: PreviewComponentRendererProps) => {
	const props = component.custom as {
		label: string
		labelColor: string
		labelWeight?: 'regular' | 'semibold' | 'hidden'
		min?: number
		max?: number
		backgroundColor?: string
	}

	// Find template container (has no evaluationSubmissionId)
	const templateContainer = component.children?.find((child) => child.name === 'container' && !child.evaluationId)
	const columns = getColumns(component.custom as Record<string, unknown>)
	const effectiveMax = props.max != null && props.max > 0 ? props.max : undefined

	// All containers to display (template + persisted instances + temp instances)
	const allInstanceContainers = [...(templateContainer ? [templateContainer] : [])]

	const currentInstanceCount = allInstanceContainers.length

	return (
		<div
			id={component.id}
			className={cn(
				'flex flex-col gap-4 my-4 p-6 border rounded-lg border-neutral-200 bg-white',
				props.backgroundColor && 'rounded-md',
			)}
			style={{ backgroundColor: props.backgroundColor || undefined }}>
			<div className='flex items-center justify-between gap-2'>
				<div className='flex items-center gap-2 flex-1'>
					{props.label && (
						<Text
							variant='h6'
							weight={(props.labelWeight !== 'hidden' && props.labelWeight) || null}
							className={cn(props.labelWeight === 'hidden' && 'hidden')}
							style={{ color: props.labelColor }}>
							{props.label}
						</Text>
					)}
					<span className='px-2 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded'>
						{currentInstanceCount} of {effectiveMax ?? '∞'}
					</span>
				</div>
			</div>

			{/* Render instance containers */}
			<div className='flex flex-col gap-4'>
				{currentInstanceCount === 0 && (
					<Text variant='small' className='text-neutral-500 italic'>
						No instances yet. Click "+ Add" to create one.
					</Text>
				)}

				{/* Render each instance container */}
				{allInstanceContainers.map((instanceContainer) => {
					const visibleChildren = instanceContainer.children?.filter((c) => c.action !== 'delete') || []
					return (
						<div
							key={instanceContainer.id}
							className='flex flex-col gap-2 p-4 border border-neutral-200 rounded-md bg-white'>
							{columns > 1 ? (
								<div className='grid gap-4' style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
									{groupChildrenByColumn(visibleChildren, columns).map((colChildren, colIdx) => (
										// biome-ignore lint/suspicious/noArrayIndexKey: columns are positional with no stable identifier
										<div key={colIdx} className='flex flex-col gap-2'>
											{colChildren.map((child) => (
												<PreviewComponentRenderer key={child.id} component={child} control={control} />
											))}
										</div>
									))}
								</div>
							) : (
								visibleChildren.map((child) => (
									<PreviewComponentRenderer key={child.id} component={child} control={control} />
								))
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}

/**
 * Average component with support for preview
 */
function PreviewAverageComponent({ component, control, allComponents }: PreviewComponentRendererProps) {
	const { selectedComponentIds } = component.custom as {
		selectedComponentIds: string[]
	}

	const watchedValues = useWatch({
		control,
		name: selectedComponentIds || [],
	})

	const averageValue = useMemo(() => {
		if (!selectedComponentIds || selectedComponentIds.length === 0 || !allComponents) {
			return null
		}

		const findComponentById = (id: string, components: FilteredComponent[]): FilteredComponent | null => {
			for (const comp of components) {
				if (comp.id === id) return comp
				if (comp.children) {
					const found = findComponentById(id, comp.children)
					if (found) return found
				}
			}
			return null
		}

		const allValues: number[] = []

		for (let index = 0; index < selectedComponentIds.length; index++) {
			const componentId = selectedComponentIds[index]
			const foundComponent = findComponentById(componentId, allComponents)

			if (!foundComponent) continue

			if (foundComponent.mockReports && foundComponent.mockReports.length > 0) {
				for (const report of foundComponent.mockReports) {
					if (report.average) {
						const numValue = Number.parseFloat(report.average)
						if (!Number.isNaN(numValue) && Number.isFinite(numValue)) {
							allValues.push(numValue)
						}
					}
				}
			} else {
				const watchedValue = watchedValues?.[index]
				if (watchedValue !== undefined && watchedValue !== null && watchedValue !== '') {
					const numValue = typeof watchedValue === 'number' ? watchedValue : Number.parseFloat(String(watchedValue))
					if (!Number.isNaN(numValue) && Number.isFinite(numValue)) {
						allValues.push(numValue)
					}
				}
			}
		}

		if (allValues.length > 0) {
			return allValues.reduce((acc, val) => acc + val, 0) / allValues.length
		}

		return null
	}, [watchedValues, selectedComponentIds, allComponents])

	return <FormsAverage component={component} value={averageValue} />
}

/**
 * Main component renderer for preview mode
 */
export function PreviewComponentRenderer({ component, control, allComponents }: PreviewComponentRendererProps) {
	const fieldName = component.id
	const readOnly = !component.write
	const required = !component.optional
	const hasReports = component.mockReports && component.mockReports.length > 0
	const shouldShowInput = !component.reportOnly && component.write

	switch (component.name) {
		case 'text': {
			return (
				<>
					{hasReports && <PreviewReports reports={component.mockReports as Report[]} />}
					{shouldShowInput && (
						<Controller
							control={control}
							name={fieldName}
							defaultValue=''
							render={({ field }) => (
								<div className={readOnly ? 'opacity-60 pointer-events-none' : ''}>
									<FormsTextField
										component={component}
										value={field.value as string}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
										readOnly={readOnly}
										required={required}
										hasError={false}
									/>
								</div>
							)}
						/>
					)}
				</>
			)
		}

		case 'rating': {
			return (
				<>
					{shouldShowInput && (
						<Controller
							control={control}
							name={fieldName}
							defaultValue=''
							render={({ field }) => (
								<div className={readOnly ? 'opacity-60 pointer-events-none' : ''}>
									<FormsRating
										component={component}
										value={field.value as string}
										onChange={(value: string) => field.onChange(value)}
										readOnly={readOnly}
										required={required}
										hasError={false}
									/>
								</div>
							)}
						/>
					)}
					{hasReports && <PreviewReports reports={component.mockReports as Report[]} showAverage />}
				</>
			)
		}

		case 'checkbox': {
			return (
				<>
					{hasReports && <PreviewReports reports={component.mockReports as Report[]} />}
					{shouldShowInput && (
						<Controller
							control={control}
							name={fieldName}
							defaultValue={false}
							render={({ field }) => (
								<FormsCheckbox
									component={component}
									checked={field.value as boolean}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.checked)}
									readOnly={readOnly}
									hasError={false}
									required={required}
								/>
							)}
						/>
					)}
				</>
			)
		}

		case 'number': {
			return (
				<>
					{hasReports && <PreviewReports reports={component.mockReports as Report[]} showAverage />}
					{shouldShowInput && (
						<Controller
							control={control}
							name={fieldName}
							defaultValue=''
							render={({ field }) => (
								<FormsNumberField
									component={component}
									value={String(field.value)}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const val = e.target.value
										if (val === '') {
											field.onChange('')
											return
										}
										const numVal = Number.parseFloat(val)
										if (!Number.isNaN(numVal)) {
											field.onChange(numVal)
										}
									}}
									readOnly={readOnly}
									required={required}
									hasError={false}
								/>
							)}
						/>
					)}
				</>
			)
		}

		case 'textarea': {
			return (
				<>
					{hasReports && <PreviewReports reports={component.mockReports as Report[]} />}
					{shouldShowInput && (
						<Controller
							control={control}
							name={fieldName}
							defaultValue=''
							render={({ field }) => (
								<div className={readOnly ? 'opacity-60 pointer-events-none' : ''}>
									<FormsTextArea
										component={component}
										value={field.value as string}
										onChange={(value: string) => field.onChange(value)}
										readOnly={readOnly}
										required={required}
										hasError={false}
									/>
								</div>
							)}
						/>
					)}
				</>
			)
		}

		case 'select': {
			return (
				<>
					{hasReports && <PreviewReports reports={component.mockReports as Report[]} />}
					{shouldShowInput && (
						<Controller
							control={control}
							name={fieldName}
							defaultValue=''
							render={({ field }) => (
								<FormsSelectField
									component={component}
									value={field.value as string}
									onChange={(value: string) => field.onChange(value)}
									readOnly={readOnly}
									required={required}
									hasError={false}
								/>
							)}
						/>
					)}
				</>
			)
		}

		case 'container':
			return <PreviewContainerComponent component={component} control={control} allComponents={allComponents} />

		case 'heading':
			return <FormsHeader component={component} />

		case 'instructions':
			return <FormsInstructions component={component} />

		case 'paragraph':
			return <FormsParagraph component={component} />

		case 'divider':
			return <FormsDivider component={component} />

		case 'reviewviewer': {
			const customLinkText =
				((component.custom as Record<string, unknown>)?.linkText as string) || 'View Performance Evaluation'
			const reviewViewerComponent = {
				...component,
				custom: {
					...(component.custom as Record<string, unknown>),
					linkText: customLinkText,
				},
			}
			return (
				<div className='opacity-60'>
					<FormsReviewViewer component={reviewViewerComponent} signedUrl={undefined} showUnavailableHint={false} />
					<Text variant='xsmall' className='text-neutral-400 mt-1'>
						(PDF viewer unavailable in preview)
					</Text>
				</div>
			)
		}

		case 'externallink':
			return <FormsExternalLink component={component} isPreview />

		case '9boxrating':
			return (
				<Controller
					control={control}
					name={fieldName}
					defaultValue={undefined}
					render={({ field }) => (
						<Forms9BoxRating
							component={component}
							value={field.value as number}
							onChange={(value: number) => field.onChange(value)}
							required={required}
							hasError={false}
						/>
					)}
				/>
			)

		case 'average':
			return <PreviewAverageComponent component={component} control={control} allComponents={allComponents} />

		case 'repeatable':
			return <PreviewRepeatableComponent component={component} control={control} allComponents={allComponents} />

		case 'radio-group': {
			return (
				<>
					{shouldShowInput && (
						<Controller
							control={control}
							name={fieldName}
							defaultValue=''
							render={({ field }) => (
								<ChoiceSelectField
									component={component}
									value={field.value as string}
									onChange={(value: string) => field.onChange(value)}
									readOnly={readOnly}
									required={required}
									hasError={false}
								/>
							)}
						/>
					)}
				</>
			)
		}

		case 'image':
			return <FormsImage component={component} />

		case 'dataviewer':
			return <FormsDataViewer component={component} />

		case 'aggregation':
			return <FormsAggregationPreview component={component} />

		case 'external_data_view':
			return <FormsExternalDataView component={component} preview />

		default:
			return (
				<Text variant='small' className='text-neutral-400'>
					Unknown component: {component.name}
				</Text>
			)
	}
}
